import {AxiosError} from "axios";
import {DEFAULT_PORTAL_LOGIN_KEYS, PortalLoginTranslationKeys} from "components/src/auth/PortalTranslationKeys";
import {SavedLocation} from "components/src/auth/SavedLocation";
import {XSRF} from "components/src/auth/Xsrf";
import {FloatingLabel} from "components/src/common/FloatingLabel";
import {LocaleSelector} from "components/src/common/LocaleSelector";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import DOMPurify from "dompurify";
import {useEffect, useRef, useState} from "react";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import {useSearchParams} from "react-router-dom";

/**
 * A single OIDC end-point the user can log in with.
 */
interface OidcEndPoint {
    clientId?: string;
    clientName?: string;
    loginRedirectUri?: string;
}

interface PortalLoginProps {
    /**
     * Loads the available OIDC end-points. Optional: when not provided, OIDC login is not
     * shown. Provided by the consuming app so this component can be reused with different
     * back-end methods.
     */
    getOIDCClients?: () => Promise<{data: OidcEndPoint[]}>;
    /**
     * Overrides for the i18next keys. Only the keys that need to differ have to be provided;
     * the rest fall back to {@link DEFAULT_PORTAL_LOGIN_KEYS}.
     */
    translationKeys?: Partial<PortalLoginTranslationKeys>;
}

export const PortalLogin = ({getOIDCClients, translationKeys}: PortalLoginProps = {}) =>
{
    const keys = {...DEFAULT_PORTAL_LOGIN_KEYS, ...translationKeys};

    const [searchParams] = useSearchParams();

    /**
     * A ref to the hidden _csrf form field
     */
    const csrFieldRef = useRef<HTMLInputElement>(null);

    const loginURL: string = "/api/login"

    /**
     * The OIDC end-points
     */
    const [oidcEndPoints, setOidcEndPoints] = useState<OidcEndPoint[]>();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
    }

    // back-end will add the logout parameter when the user is logged out
    if (searchParams.has("logout")) {
        SavedLocation.clearSavedLocation();
    }

    function onSubmit()
    {
        if (csrFieldRef.current !== null) {
            csrFieldRef.current.value = XSRF.ensureCookie();
        }
    }

    useEffect(() =>
    {
        // OIDC support is optional; only load end-points when a loader is provided
        if (getOIDCClients === undefined) {
            return;
        }

        getOIDCClients()
            .then(response =>
            {
                setOidcEndPoints(response.data)
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OIDC clients", err);
            })
    }, [getOIDCClients]);

    return (
        <>
        {t([keys.header]).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t([keys.header]))} />}
        <Form method="post" action={loginURL} onSubmit={onSubmit} className="w-100 p-4 shadow-sm rounded bg-body">
            <div className="mb-4 text-center">
                <h2>{t(keys.pleaseLogin)}</h2>
            </div>

            {searchParams.has("error") && <Alert variant="danger">{t(keys.loginFailure)}</Alert>}

            <FloatingLabel label={t(keys.emailLabel)} className="text-secondary mb-3">
                <Form.Control type="email" name="username" placeholder={t(keys.emailLabel)} autoFocus/>
            </FloatingLabel>

            <FloatingLabel label={t(keys.passwordLabel)} className="text-secondary mb-3">
                <Form.Control type="password" name="password" placeholder={t(keys.passwordLabel)}/>
            </FloatingLabel>

            <a href="/forgot-password">{t(keys.forgotPassword)}</a>

            {oidcEndPoints !== undefined && oidcEndPoints.length > 0 && (
                <>
                    <Form.Text id="oidcHelpBlock" muted className="mt-4 mb-2 d-block">{t(keys.oidcLogin)}</Form.Text>
                    {oidcEndPoints.map(endpoint => (
                        <div key={endpoint.clientId} className="mb-1">
                            <a href={endpoint.loginRedirectUri}>{endpoint.clientName}</a>
                        </div>
                    ))}
                </>
            )}

            <Button variant="success" type="submit" className="w-100 mt-4">
                {t(keys.loginButton)}
            </Button>

            <LocaleSelector className="w-auto mt-4" />

            {t([keys.legalNotice]).length > 0 &&
                <div className="mt-2" dangerouslySetInnerHTML={sanitizeHTML(t([keys.legalNotice]))} />
            }

            <input name="_csrf" type="hidden" ref={csrFieldRef} value=""/>
        </Form>
        {t([keys.footer]).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t([keys.footer]))} />}
        </>
    );
}
